import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { toast as message } from 'sonner';
import EnhancedDialog from '../EnhancedDialog';
import { supabaseClient } from '../../utils/supabase';
import { queryPolicies } from '../../utils/queryPolicies';

// Manual rep <-> login link (public.rep.app_user). Rep-scoped users (Opener /
// Closer) see their deals through this link; the database links by email or
// a unique name on its own, and this dialog covers the rest: names that
// differ, a login with no rep record yet, moving a login to another rep.
// The server functions (core_crm.set_rep_login / create_rep_for_login) check
// the Manage Users permission and keep one active rep per login.

interface LinkRep { id: number; name: string | null; email: string | null; is_active: boolean | null; app_user: number | null }
interface LinkLogin { id: number; name: string | null; email: string | null }

export type RepLoginLinkTarget =
  | { kind: 'user'; id: number; label: string }
  | { kind: 'rep'; id: number; label: string };

const repLoginLinkOptionsQueryKey = ['rep-login-link-options'] as const;

async function loadLinkOptions(): Promise<{ reps: LinkRep[]; logins: LinkLogin[] }> {
  const [reps, logins] = await Promise.all([
    (supabaseClient as any).from('rep').select('id, name, email, is_active, app_user').order('name'),
    (supabaseClient as any).schema('core_crm').from('app_user')
      .select('id, name, email')
      .is('suspended_at', null)
      .not('is_system', 'is', true)
      .order('name'),
  ]);
  if (reps.error) throw reps.error;
  if (logins.error) throw logins.error;
  return { reps: reps.data ?? [], logins: logins.data ?? [] };
}

const loginLabel = (login: LinkLogin | undefined): string =>
  login ? `${login.name || login.email || `Login #${login.id}`}${login.name && login.email ? ` (${login.email})` : ''}` : '';

async function rpc(fn: 'set_rep_login' | 'create_rep_for_login', params: Record<string, unknown>): Promise<void> {
  const { error } = await (supabaseClient as any).schema('core_crm').rpc(fn, params);
  if (error) throw new Error(error.message || 'Could not save the link.');
}

export default function RepLoginLinkDialog({ target, onClose, onSaved }: {
  target: RepLoginLinkTarget;
  onClose: () => void;
  onSaved: () => Promise<unknown> | void;
}): React.ReactElement {
  const options = useQuery({ queryKey: repLoginLinkOptionsQueryKey, queryFn: loadLinkOptions, ...queryPolicies.configuration, staleTime: 0 });
  const reps = useMemo(() => options.data?.reps ?? [], [options.data]);
  const logins = useMemo(() => options.data?.logins ?? [], [options.data]);
  const loginsById = useMemo(() => new Map(logins.map((login) => [login.id, login])), [logins]);
  const activeReps = reps.filter((rep) => rep.is_active !== false);
  const [choice, setChoice] = useState<string>('');
  const [saving, setSaving] = useState(false);

  // Current link, seen from the side being edited.
  const currentRep = target.kind === 'user' ? activeReps.find((rep) => rep.app_user === target.id) : reps.find((rep) => rep.id === target.id);
  const currentLoginId = target.kind === 'rep' ? currentRep?.app_user ?? null : target.id;
  const linked = target.kind === 'user' ? !!currentRep : currentLoginId != null;

  const picked = choice ? Number(choice) : null;
  let note = '';
  if (picked != null && target.kind === 'user') {
    const rep = reps.find((r) => r.id === picked);
    const owner = rep?.app_user != null && rep.app_user !== target.id ? loginsById.get(rep.app_user) : undefined;
    if (currentRep && currentRep.id !== picked) note = `The login moves off ${currentRep.name}.`;
    if (owner) note = `${note} ${rep?.name} is linked to ${loginLabel(owner)} now; that link is replaced.`.trim();
  }
  if (picked != null && target.kind === 'rep') {
    const other = activeReps.find((rep) => rep.app_user === picked && rep.id !== target.id);
    if (other) note = `This login is linked to ${other.name} now. Saving moves it here and unlinks ${other.name}.`;
  }

  const run = async (work: () => Promise<void>, done: string) => {
    setSaving(true);
    try {
      await work();
      await options.refetch();
      await onSaved();
      message.success(done);
      onClose();
    } catch (error) {
      message.error((error as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const save = () => {
    if (picked == null) return;
    if (target.kind === 'user') {
      // The admin picked this login's rep here, so moving off a current rep is the intent.
      void run(() => rpc('set_rep_login', { p_rep: picked, p_user: target.id, p_move: true }), 'Rep linked.');
    } else {
      void run(() => rpc('set_rep_login', { p_rep: target.id, p_user: picked, p_move: !!note }), 'Login linked.');
    }
  };
  const unlink = () => {
    const repId = target.kind === 'user' ? currentRep?.id : target.id;
    if (repId == null) return;
    void run(() => rpc('set_rep_login', { p_rep: repId, p_user: null }), 'Unlinked.');
  };
  const createRep = () => {
    if (target.kind !== 'user') return;
    void run(() => rpc('create_rep_for_login', { p_user: target.id }), 'Rep created and linked.');
  };

  const currentText = target.kind === 'user'
    ? currentRep?.name || 'No rep'
    : currentLoginId != null ? loginLabel(loginsById.get(currentLoginId)) || `Login #${currentLoginId}` : 'No login';

  return (
    <EnhancedDialog
      isOpen
      onClose={() => { if (!saving) onClose(); }}
      canOutsideClickClose={!saving}
      title={target.kind === 'user' ? `Rep for ${target.label}` : `Login for ${target.label}`}
    >
      <div className="sky-dialog-body" style={{ display: 'grid', gap: 12, minWidth: 380 }}>
        <p style={{ margin: 0 }}>
          Linked now: <strong>{options.isLoading ? 'Loading…' : currentText}</strong>
        </p>
        <p style={{ margin: 0, color: 'var(--text-3)', fontSize: 12 }}>
          Openers and Closers see the deals of the rep their login is linked to.
        </p>
        <div className="sky-form-group">
          <label className="sky-form-label" htmlFor="rep-login-link-choice">
            {target.kind === 'user' ? 'Link to rep' : 'Link to login'}
          </label>
          <select
            id="rep-login-link-choice"
            className="sky-field-input"
            value={choice}
            disabled={saving || options.isLoading}
            onChange={(event) => setChoice(event.target.value)}
          >
            <option value="">{options.isLoading ? 'Loading…' : 'Choose…'}</option>
            {target.kind === 'user'
              ? activeReps.filter((rep) => rep.id !== currentRep?.id).map((rep) => {
                const owner = rep.app_user != null ? loginsById.get(rep.app_user) : undefined;
                return <option key={rep.id} value={rep.id}>{rep.name || `Rep #${rep.id}`}{owner ? ` (linked to ${owner.name || owner.email})` : ''}</option>;
              })
              : logins.filter((login) => login.id !== currentLoginId).map((login) => {
                const other = activeReps.find((rep) => rep.app_user === login.id);
                return <option key={login.id} value={login.id}>{loginLabel(login)}{other ? ` (rep: ${other.name})` : ''}</option>;
              })}
          </select>
        </div>
        {note ? <p style={{ margin: 0, color: 'var(--amber)', fontSize: 12 }}>{note}</p> : null}
        {options.error ? <p style={{ margin: 0, color: 'var(--red)' }}>{(options.error as Error).message}</p> : null}
      </div>
      <div className="sky-dialog-footer">
        {linked ? (
          <button className="sky-btn sky-btn-danger" disabled={saving} onClick={unlink} style={{ marginRight: 'auto' }}>Unlink</button>
        ) : target.kind === 'user' ? (
          <button
            className="sky-btn sky-btn-ghost"
            disabled={saving || options.isLoading}
            onClick={createRep}
            style={{ marginRight: 'auto' }}
            title="Create a rep record named after this login, with its email, and link it"
          >
            Create rep from this login
          </button>
        ) : null}
        <button className="sky-btn sky-btn-ghost" disabled={saving} onClick={onClose}>Cancel</button>
        <button className="sky-btn sky-btn-primary" disabled={saving || picked == null} onClick={save}>
          {saving ? <span className="sky-spinner" /> : null}
          {note ? 'Move and link' : 'Link'}
        </button>
      </div>
    </EnhancedDialog>
  );
}
